/**
 * InputTextPanel.jsx
 *
 * Panel for entering and displaying input text, tokenized output, and token coloring options.
 * Part of the text embedding visualization UI.
 *
 * Copyright (c) 2023-2025, text-embedding-visualization authors.
 * Licensed under the MIT License.
 */

import React from 'react';
import { DisplayParamTokenColorMode } from '../constants';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import IconButton from '@mui/material/IconButton';
import Collapse from '@mui/material/Collapse';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';

/**
 * Panel for entering input text, displaying tokenized output, and toggling token coloring.
 *
 * @component
 * @param {Object} props
 * @param {string} props.inputText - The current input text value.
 * @param {Array} props.tokens - Array of token objects ({id, text, ...}).
 * @param {boolean} props.showTokenIds - Whether to display token IDs instead of text.
 * @param {function} props.onInputTextChange - Handler for input text changes.
 * @param {function} props.onToggleShowTokenIds - Handler for toggling token ID display.
 * @param {boolean} props.isTokenizing - Whether tokenization is in progress.
 * @param {boolean} props.inputTextOpen - Whether the panel is expanded.
 * @param {function} props.setInputTextOpen - Handler to toggle panel open state.
 * @param {boolean} props.showColorTokens - Whether to color code tokens.
 * @param {function} props.setShowColorTokens - Handler to toggle token coloring.
 * @param {string} props.textPanel_tokenColorMode - Token coloring mode ('id' or 'sequence').
 * @param {function} props.getTokenColor - Function to get color for a token ID.
 * @param {function} props.getSequenceColor - Function to get color for a token index.
 */
export default function InputTextPanel({
  inputText,
  tokens,
  showTokenIds,
  onInputTextChange,
  onToggleShowTokenIds,
  isTokenizing,
  inputTextOpen,
  setInputTextOpen,
  showColorTokens,
  setShowColorTokens,
  textPanel_tokenColorMode,
  getTokenColor,
  getSequenceColor
}) {
  return (
    <Box
      mb={0.5}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
        border: '1px solid',
        borderColor: 'grey.300',
        backgroundColor: 'rgba(255,255,255,0.85)',
        borderRadius: 2,
        padding: 1,
      }}
    >
      {/* Header row: title, token/word counts, help, expand/collapse */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
        <Box display="flex" alignItems="center">
          <Typography variant="subtitle1" fontWeight={600}>
            Input Text
          </Typography>
          {/* Token and word counts */}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ ml: 1, fontWeight: 400, fontSize: '0.75em' }}
          >
            Tokens: <b>{tokens?.length ?? 0}</b>
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ ml: 2, fontWeight: 400, fontSize: '0.75em' }}
          >
            Words: <b>{inputText ? inputText.trim().split(/\s+/).filter(Boolean).length : 0}</b>
          </Typography>
          {/* Show progress indicator if tokenizing */}
          {isTokenizing && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
              <CircularProgress size={18} />
              <span style={{ fontSize: '0.85em' }}>Tokenizing...</span>
            </Box>
          )}
        </Box>
        {/* Help tooltip and expand/collapse button */}
        <Box display="flex" alignItems="center">
          <Tooltip
            title="Enter or paste the text you want to analyze. The text will be tokenized and divided into chunks for embedding visualization."
            placement="top"
            arrow
          >
            <HelpOutlineIcon
              fontSize="small"
              sx={{ color: 'text.secondary', cursor: 'pointer', mr: 1 }}
            />
          </Tooltip>
          <IconButton
            size="small"
            onClick={() => setInputTextOpen((open) => !open)}
            aria-label={inputTextOpen ? "Collapse" : "Expand"}
            sx={{
              transform: inputTextOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
              transition: 'transform 0.2s',
            }}
          >
            <ExpandMoreIcon />
          </IconButton>
        </Box>
      </Box>
      {/* Collapsible input and token display area */}
      <Collapse in={inputTextOpen}>
        {inputTextOpen && (
          <>
            {/* Token color display or plain textarea */}
            {showColorTokens ? (
              <Box
                sx={{
                  width: '100%',
                  minHeight: 120,
                  maxHeight: 200,
                  fontFamily: 'monospace',
                  fontSize: '0.75rem',
                  borderRadius: 1,
                  border: '1px solid #ccc',
                  padding: '4px',
                  background: '#fff',
                  overflowY: 'auto',
                  mb: 1,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  lineHeight: 1.5,
                  boxSizing: 'border-box',
                }}
              >
                {/* Render each token with background color based on textPanel_tokenColorMode */}
                {tokens && tokens.length > 0
                  ? tokens.map((tok, i) => {
                      let textBackgroundColor = '#ffffffff';
                      let textColor = 'black';
                      if (textPanel_tokenColorMode === DisplayParamTokenColorMode.TOKEN_ID) {
                        textBackgroundColor = typeof getTokenColor === 'function'
                          ? getTokenColor(tok.id)
                          : `hsl(${(tok.id * 47) % 360},70%,85%)`;
                      } else if (textPanel_tokenColorMode === DisplayParamTokenColorMode.SEQUENCE) {
                        textBackgroundColor = typeof getSequenceColor === 'function'
                          ? getSequenceColor(i, tokens.length)
                          : `hsl(${(i * 47) % 360},70%,85%)`;
                      } else if (textPanel_tokenColorMode === DisplayParamTokenColorMode.BLACK) {
                        textBackgroundColor = '#000000';
                        textColor = '#ffffff';
                      } else if (textPanel_tokenColorMode === DisplayParamTokenColorMode.WHITE) {
                        textBackgroundColor = '#ffffff';
                        textColor = '#000000';
                      }
                      else if (textPanel_tokenColorMode === DisplayParamTokenColorMode.NONE) {
                        textColor = 'black';
                      } else {
                        textBackgroundColor = '#cccccc';
                      }

                      return (
                        <span
                          key={i}
                          style={{
                            color: textColor,
                            backgroundColor: textBackgroundColor,
                            borderRadius: 0,
                            fontFamily: 'monospace',
                            fontSize: '0.75rem',
                            lineHeight: 1.5,
                            display: 'inline',
                          }}
                          title={`Token ID: ${tok.id}`}
                        >
                          {showTokenIds ? `[${tok.id}]` : tok.text}
                        </span>
                      );
                    })
                  : <span style={{ color: '#aaa' }}>No tokens to display.</span>
                }
              </Box>
            ) : (
              // Plain textarea for input
              <textarea
                value={inputText}
                onChange={e => onInputTextChange(e.target.value)}
                style={{
                  width: '100%',
                  minHeight: 120,
                  maxHeight: 200,
                  fontFamily: 'monospace',
                  fontSize: '0.75rem',
                  borderRadius: 4,
                  border: '1px solid #ccc',
                  padding: 4,
                  boxSizing: 'border-box',
                  resize: 'vertical',
                  background: '#fff',
                  overflowY: 'auto',
                  lineHeight: 1.5,
                }}
                placeholder="Enter your text here…"
              />
            )}
            {/* Token coloring and ID display toggles */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'left', gap: 0, mt: 0, fontSize: '0.75rem'}}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showColorTokens}
                    onChange={e => setShowColorTokens(e.target.checked)}
                    size="small"
                    disabled={!tokens || tokens.length === 0}
                  />
                }
                label="Color code the tokens"
                sx={{ fontSize: 'inherit' }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showTokenIds}
                    onChange={e => onToggleShowTokenIds(e.target.checked)}
                    size="small"
                    disabled={!showColorTokens || !tokens || tokens.length === 0}
                  />
                }
                label="Display token IDs instead of text"
                sx={{ fontSize: '0.5em' }}
              />
            </Box>
          </>
        )}
      </Collapse>
    </Box>
  );
}
